/**
 * telemetry.ts — Structured behavioral signals for CHIRON personalization.
 *
 * Writes to public.workout_events. Distinct from heliosTelemetry (loose
 * analytics → user_events). This module captures the strict 8-event
 * discriminated union that feeds personalization read paths (rest-time
 * autoreg, SM-2 scheduling, mastery curves, etc.).
 *
 * Contract:
 *   - emitEvent() is fire-and-forget. NEVER throws.
 *   - On insert failure, queues to IndexedDB via idb-keyval.
 *   - flushRetryQueue() drains the queue; startAutoFlush() listens for `online`.
 *   - Idempotent: server unique index on (user_id, client_event_id) dedupes retries.
 */

import { get, set as idbSet } from 'idb-keyval';
import { supabase } from '../lib/supabase';

// ── Schema ───────────────────────────────────────────────────────────

interface BaseEvent {
  ts: string;             // ISO 8601, client-generated
  sessionId?: string;     // UUID, set for session-scoped events
  clientEventId: string;  // UUID, idempotency key
  _v: 1;                  // schema version, additive-only contract
}

export type UserEvent =
  | SessionStartedEvent
  | SessionCompletedEvent
  | SessionAbandonedEvent
  | SetLoggedEvent
  | ExerciseSkippedEvent
  | ExerciseSwappedEvent
  | PrescriptionModifiedEvent
  | PostExerciseFeedbackEvent;

export interface SessionStartedEvent extends BaseEvent {
  type: 'session_started';
  prescriptionId: string | null;
}

export interface SessionCompletedEvent extends BaseEvent {
  type: 'session_completed';
  durationSec: number;
  setsLogged: number;
  setsPrescribed: number;
}

export interface SessionAbandonedEvent extends BaseEvent {
  type: 'session_abandoned';
  lastExerciseIdx: number;
  lastSetIdx: number;
  setsLogged: number;
}

export interface SetLoggedEvent extends BaseEvent {
  type: 'set_logged';
  exerciseId: string;
  weightKg: number;
  reps: number;
  /** Reps in reserve, integer 0-5. 0 = failure, 5 = trivial. null until post-set feedback wires up (Wave 2). */
  rir: number | null;
  /** Seconds since previous set on this exercise. null on first set or if not tracked. */
  restSec: number | null;
  prescribedWeightKg: number | null;
  prescribedReps: number | null;
}

export type SkipReasonCode =
  | 'pain_or_injury'
  | 'no_equipment'
  | 'no_time'
  | 'too_hard'
  | 'too_easy'
  | 'technique_issue'
  | 'prefer_alternative'
  | 'other';

export interface ExerciseSkippedEvent extends BaseEvent {
  type: 'exercise_skipped';
  exerciseId: string;
  reasonCode: SkipReasonCode;
  reasonText?: string;
}

export interface ExerciseSwappedEvent extends BaseEvent {
  type: 'exercise_swapped';
  fromExerciseId: string;
  toExerciseId: string;
  reasonCode: SkipReasonCode;
  reasonText?: string;
}

export interface PrescriptionModifiedEvent extends BaseEvent {
  type: 'prescription_modified';
  exerciseId: string;
  field: 'weight' | 'reps' | 'sets' | 'rest';
  fromValue: number;
  toValue: number;
}

export interface PostExerciseFeedbackEvent extends BaseEvent {
  type: 'post_exercise_feedback';
  exerciseId: string;
  /** 4-tap question scores. null = user skipped that tap. */
  effort: number | null;
  technique: number | null;
  motivation: number | null;
  pain: number | null;
}

// ── Public API ───────────────────────────────────────────────────────

/**
 * Construct a typed event with auto-generated ts and clientEventId.
 * Use at every call site so timestamps and idempotency keys are consistent.
 */
export function newEvent<T extends UserEvent['type']>(
  type: T,
  fields: Omit<Extract<UserEvent, { type: T }>, 'type' | 'ts' | 'clientEventId' | '_v'>,
): Extract<UserEvent, { type: T }> {
  return {
    type,
    ts: new Date().toISOString(),
    clientEventId: uuid(),
    _v: 1,
    ...fields,
  } as Extract<UserEvent, { type: T }>;
}

/** Fire-and-forget. Never throws. */
export function emitEvent(event: UserEvent): void {
  void writeEvent(event);
}

/** Drain the retry queue. Returns count of events flushed. Never throws. */
export async function flushRetryQueue(): Promise<number> {
  try {
    const queue = await getQueue();
    if (queue.length === 0) return 0;

    const { data: authData } = await supabase.auth.getUser();
    const userId = authData.user?.id;
    if (!userId) return 0;

    const rows = queue.map(toRow(userId));
    const { error } = await supabase
      .from(TABLE)
      .upsert(rows, { onConflict: 'user_id,client_event_id', ignoreDuplicates: true });

    if (error) return 0;

    await setQueue([]);
    return queue.length;
  } catch {
    return 0;
  }
}

let autoFlushAttached = false;

/** Wire `online` listener + flush on mount. Idempotent. Call once at app start. */
export function startAutoFlush(): void {
  if (autoFlushAttached) return;
  if (typeof window === 'undefined') return;
  autoFlushAttached = true;

  void flushRetryQueue();
  window.addEventListener('online', () => { void flushRetryQueue(); });
}

// ── Internals ────────────────────────────────────────────────────────

const TABLE = 'workout_events';
const QUEUE_KEY = 'helios_workout_event_retry_queue';
const QUEUE_CAP = 100;

function uuid(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const toRow = (userId: string) => (e: UserEvent) => ({
  user_id: userId,
  type: e.type,
  payload: e,
  ts: e.ts,
  session_id: e.sessionId ?? null,
  client_event_id: e.clientEventId,
});

async function getQueue(): Promise<UserEvent[]> {
  return (await get<UserEvent[]>(QUEUE_KEY)) ?? [];
}

async function setQueue(queue: UserEvent[]): Promise<void> {
  await idbSet(QUEUE_KEY, queue);
}

async function writeEvent(event: UserEvent): Promise<void> {
  try {
    const { data: authData } = await supabase.auth.getUser();
    const userId = authData.user?.id;
    if (!userId) return;

    const { error } = await supabase.from(TABLE).insert(toRow(userId)(event));
    if (error) await enqueueRetry(event);
  } catch {
    await enqueueRetry(event).catch(() => {});
  }
}

async function enqueueRetry(event: UserEvent): Promise<void> {
  try {
    const queue = await getQueue();
    queue.push(event);
    while (queue.length > QUEUE_CAP) queue.shift();
    await setQueue(queue);
  } catch {
    // IndexedDB unavailable. workout_logs is the canonical record.
  }
}
